import React, { useMemo } from "react";
import { Collapse, Switch, InputNumber, Select, Tag, Tooltip } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";

const { Panel } = Collapse;

export const DIMENSION_LABELS = {
  SANCTIONS: "Sanctions",
  FINANCIAL_CRIME: "Financial Crime",
  BLOCKCHAIN: "Blockchain Risk",
  CUSTOMER: "Customer Risk",
  GEOGRAPHIC: "Geographic Risk",
  BEHAVIORAL: "Behavioral Risk",
};

const DIMENSION_ORDER = [
  "SANCTIONS", "FINANCIAL_CRIME", "BLOCKCHAIN", "CUSTOMER", "GEOGRAPHIC", "BEHAVIORAL",
];

const ACTION_OPTIONS = [
  { value: "MONITOR", label: "Monitor" },
  { value: "HOLD", label: "Hold" },
  { value: "REJECT", label: "Reject" },
];

const TRIGGER_OPTIONS = [
  { value: "OWNERSHIP", label: "Ownership" },
  { value: "COUNTERPARTY", label: "Counterparty" },
  { value: "INDIRECT", label: "Indirect" },
];

/**
 * Editor for the weighted risk-indicator policy table. Renders every catalogued indicator
 * grouped by dimension; each row has an enable toggle, weight, action, and (for on-chain TRM
 * indicators) an applies-to selector. `value` is the saved policy array; the catalog supplies
 * defaults and labels. onChange emits the full merged policy array.
 */
export default function RiskPolicyEditor({ catalog = [], value = [], onChange }) {
  // key → saved policy override
  const byKey = useMemo(() => {
    const m = {};
    for (const p of value || []) m[p.key] = p;
    return m;
  }, [value]);

  // Effective policy for a catalog entry = saved override ?? catalog default.
  const effective = (entry) => {
    const saved = byKey[entry.key];
    return {
      key: entry.key,
      dimension: entry.dimension,
      enabled: saved?.enabled ?? true,
      action: saved?.action ?? entry.defaultAction,
      weight: saved?.weight ?? entry.defaultWeight,
      appliesTo: saved?.appliesTo ?? entry.defaultAppliesTo,
    };
  };

  // Write a full policy array back (one entry per catalog key, so saving is deterministic).
  const emit = (changedKey, patch) => {
    const next = catalog.map((entry) => {
      const eff = effective(entry);
      return entry.key === changedKey ? { ...eff, ...patch } : eff;
    });
    onChange?.(next);
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const e of catalog) (g[e.dimension] ||= []).push(e);
    return g;
  }, [catalog]);

  if (!catalog.length) {
    return <div style={{ fontSize: 12, opacity: 0.6 }}>Loading risk-indicator catalog…</div>;
  }

  return (
    <div>
      <label style={{ fontSize: 13, fontWeight: 600 }}>
        Risk indicator framework{" "}
        <span style={{ fontWeight: 400, opacity: 0.6 }}>
          (weighted policy — Monitor scores only, Hold routes to review, Reject auto-declines)
        </span>
      </label>
      <Collapse style={{ marginTop: 8 }} defaultActiveKey={["SANCTIONS"]}>
        {DIMENSION_ORDER.filter((d) => grouped[d]?.length).map((dim) => {
          const entries = grouped[dim];
          const activeCount = entries.filter((e) => effective(e).enabled).length;
          return (
            <Panel
              key={dim}
              header={
                <span style={{ fontWeight: 600 }}>
                  {DIMENSION_LABELS[dim] || dim}{" "}
                  <Tag style={{ marginLeft: 6 }}>{activeCount}/{entries.length} on</Tag>
                </span>
              }
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {entries.map((entry) => {
                  const p = effective(entry);
                  const isOnChain = entry.source === "TRM";
                  return (
                    <div
                      key={entry.key}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(180px, 1.4fr) 80px 120px minmax(160px, 1fr) auto",
                        alignItems: "center",
                        gap: 10,
                        padding: "6px 8px",
                        borderRadius: 8,
                        background: p.enabled ? "#fafafa" : "transparent",
                        opacity: p.enabled ? 1 : 0.55,
                      }}
                    >
                      <span style={{ fontSize: 13 }}>{entry.label}</span>
                      <Tooltip title="Risk score contribution (0–100). Higher = more impact on the composite score when this indicator fires.">
                        <InputNumber
                          size="small"
                          min={0}
                          max={100}
                          value={p.weight}
                          onChange={(v) => emit(entry.key, { weight: v ?? 0 })}
                        />
                      </Tooltip>
                      <Tooltip title="Monitor: score only, no automatic action. Hold: send to ON_HOLD queue. Reject: auto-decline the transaction.">
                        <Select
                          size="small"
                          value={p.action}
                          options={ACTION_OPTIONS}
                          onChange={(v) => emit(entry.key, { action: v })}
                          style={{ width: "100%" }}
                        />
                      </Tooltip>
                      {isOnChain ? (
                        <Tooltip title="Ownership: address is directly flagged (strongest). Counterparty: transacted with a flagged address. Indirect: multi-hop exposure (weakest). Start with Ownership only; broaden for high-severity categories.">
                          <Select
                            size="small"
                            mode="multiple"
                            placeholder="Applies to"
                            value={p.appliesTo || ["OWNERSHIP"]}
                            options={TRIGGER_OPTIONS}
                            onChange={(v) => emit(entry.key, { appliesTo: v })}
                            style={{ width: "100%" }}
                          />
                        </Tooltip>
                      ) : (
                        <Tag color="blue" style={{ fontSize: 10 }}>{entry.source}</Tag>
                      )}
                      <Tooltip title={p.enabled ? "Indicator is active — click to disable" : "Indicator is disabled — click to enable"}>
                        <Switch
                          size="small"
                          checked={p.enabled}
                          onChange={(v) => emit(entry.key, { enabled: v })}
                        />
                      </Tooltip>
                    </div>
                  );
                })}
                <div style={{ display: "flex", gap: 16, fontSize: 10, opacity: 0.5, paddingLeft: 8 }}>
                  <span style={{ minWidth: 180 }}>Indicator</span>
                  <Tooltip title="Severity contribution (0–100). The higher the weight, the more this indicator raises the composite risk score when triggered. Recommended: 90–100 for sanctions or ransomware (ownership), 40–70 for counterparty exposure, 20–40 for indirect or behavioral signals.">
                    <span style={{ cursor: "help" }}>Weight <QuestionCircleOutlined /></span>
                  </Tooltip>
                  <Tooltip title="What happens when this indicator fires: Monitor = contributes to the score only (no automatic hold or reject), Hold = routes the transaction to ON_HOLD for admin review, Reject = auto-declines the transaction. REJECT takes precedence over HOLD across all triggered indicators.">
                    <span style={{ cursor: "help" }}>Action <QuestionCircleOutlined /></span>
                  </Tooltip>
                  <Tooltip title="Which TRM risk relationship triggers this indicator (on-chain indicators only): Ownership = the deposit/withdrawal address itself is directly linked to this category (strongest signal), Counterparty = the address has transacted with a flagged address, Indirect = multi-hop exposure through other addresses (weakest). Recommended: Ownership-only for most indicators; broaden to Counterparty for severe ones like sanctions or darknet.">
                    <span style={{ cursor: "help" }}>Applies to / source <QuestionCircleOutlined /></span>
                  </Tooltip>
                  <Tooltip title="Toggle this indicator on or off. When off, matches to this category are completely ignored — they do not contribute to the score or trigger any action.">
                    <span style={{ cursor: "help" }}>On <QuestionCircleOutlined /></span>
                  </Tooltip>
                </div>
              </div>
            </Panel>
          );
        })}
      </Collapse>
    </div>
  );
}
